import React from 'react';
import { Box } from '@interstate/components/Box';
import { InformationCircleIcon } from '@interstate/components/Icons/InformationCircleIcon';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import { CheckBox } from '@interstate/components/CheckBox';
import { Typography } from '@interstate/components/Typography';
import { SelectInput } from '@interstate/components/SelectInput';
import { NumericInput } from '@interstate/components/NumericInput';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { TextInput } from '@interstate/components/TextInput';
import { List, ListItem } from '@interstate/components/List';
import type { FourSquareDisplayKey, DollarValueRounding } from './constants';
import {
    FOUR_SQUARE_DISPLAY_LABELS,
    DOLLAR_VALUE_ROUNDING_OPTIONS,
    DOLLAR_VALUE_ROUNDING,
    TEXT_INPUT_MAX_LENGTH
} from './constants';
import PaymentRangeSection from './components/PaymentRangeSection';

type FourSquareDisplayConfig = Record<FourSquareDisplayKey, { active: boolean; displayName?: string; value?: string }>;

interface FourSquareDisplayTabProps {
    displayConfiguration: FourSquareDisplayConfig;
    onDisplayActiveChange: (displayKey: FourSquareDisplayKey, checked: boolean) => void;
    onDisplayValueChange: (displayKey: FourSquareDisplayKey, newDisplayValue: string | undefined) => void;
    onDisplayNameChange: (displayKey: FourSquareDisplayKey, newDisplayName: string) => void;
    onDollarValueRoundingChange: (value: DollarValueRounding) => void;
    disabled?: boolean;
}

const FourSquareDisplayTabBase: React.FC<FourSquareDisplayTabProps> = ({
    displayConfiguration,
    onDisplayActiveChange,
    onDisplayValueChange,
    onDisplayNameChange,
    onDollarValueRoundingChange,
    disabled
}) => {
    const { dollarValueRounding, conditionalDiscount, paymentRange, ...regularItems } = displayConfiguration;

    const listData = Object.entries(regularItems).map(([key, config]) => ({
        displayKey: key as FourSquareDisplayKey,
        label: FOUR_SQUARE_DISPLAY_LABELS[key as FourSquareDisplayKey],
        active: config.active,
        displayName: config.displayName
    }));

    const handleActiveChange = (displayKey: FourSquareDisplayKey, e: InterstateOnChangeEvent<CheckBoxEventValue>) => {
        onDisplayActiveChange(displayKey, e.target.value.checked);
    };

    const dollarValueRoundingValue = dollarValueRounding?.value ?? DOLLAR_VALUE_ROUNDING.ROUND_TO_NEAREST_DOLLAR;

    return (
        <Box>
            {!disabled && (
                <Box display="flex" alignItems="center" gap="8px" mt={2} mb={1}>
                    <InformationCircleIcon type="info" />
                    <Typography variant="body-md">Configure display settings for the 4-square template.</Typography>
                </Box>
            )}

            <List data-testid="four-square-display-configuration-table">
                {[
                    paymentRange && (
                        <PaymentRangeSection
                            key="paymentRange"
                            isActive={paymentRange.active}
                            value={paymentRange.value}
                            onDisplayActiveChange={onDisplayActiveChange as (key: string, checked: boolean) => void}
                            onDisplayValueChange={onDisplayValueChange as (key: string, value?: string) => void}
                            disabled={disabled}
                        />
                    ),
                    dollarValueRounding && (
                        <ListItem key="dollarValueRounding" id="dollarValueRounding">
                            <Box sx={{ width: '50%', py: 1 }}>
                                <SelectInput
                                    label="Dollar value rounding"
                                    name="dollarValueRounding"
                                    value={dollarValueRoundingValue}
                                    options={DOLLAR_VALUE_ROUNDING_OPTIONS.map((opt) => ({
                                        label: opt.label,
                                        value: opt.value
                                    }))}
                                    onChange={(e) => onDollarValueRoundingChange(e.target.value as DollarValueRounding)}
                                    displayDeselectOption={false}
                                    data-testid="four-square-dollar-value-rounding-select"
                                    disabled={disabled}
                                    sx={{
                                        '.MuiInputLabel-root': {
                                            color: '#000000'
                                        }
                                    }}
                                />
                            </Box>
                        </ListItem>
                    ),
                    conditionalDiscount && (
                        <ListItem key="conditionalDiscount" id="conditionalDiscount">
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    height: '64px',
                                    gap: '12px'
                                }}
                            >
                                <CheckBox
                                    value="conditionalDiscount"
                                    checked={conditionalDiscount.active}
                                    onChange={(e) => onDisplayActiveChange('conditionalDiscount', e.target.value.checked)}
                                    data-testid="four-square-display-checkbox-conditionalDiscount"
                                    disabled={disabled}
                                />
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <span>If Discount is below or equal</span>
                                    <NumericInput
                                        disabled={disabled || !conditionalDiscount.active}
                                        sx={{ maxWidth: 120, caretColor: 'auto' }}
                                        name="conditionalDiscount"
                                        displayLabel={false}
                                        placeholder="Threshold"
                                        inputPrefix={'$'}
                                        value={conditionalDiscount.value}
                                        onChange={(e) => onDisplayValueChange('conditionalDiscount', e.target.value || undefined)}
                                    />
                                    <span>do not show</span>
                                </Box>
                            </Box>
                        </ListItem>
                    ),
                    ...listData.map((item) => (
                        <ListItem key={item.displayKey} id={item.displayKey}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    height: '64px'
                                }}
                                data-display-key={item.displayKey}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <CheckBox
                                        value={`active-${item.displayKey}`}
                                        checked={item.active}
                                        onChange={(e) => handleActiveChange(item.displayKey, e)}
                                        data-testid={`four-square-display-checkbox-${item.displayKey}`}
                                        disabled={disabled}
                                    />
                                    <Box>{item.label}</Box>
                                </Box>
                                {typeof item.displayName === 'string' && (
                                    <TextInput
                                        value={item.displayName}
                                        name={item.displayKey}
                                        displayLabel={false}
                                        onChange={(e) => onDisplayNameChange(item.displayKey, e.target.value)}
                                        placeholder="Display Name"
                                        required
                                        maxLength={TEXT_INPUT_MAX_LENGTH}
                                        customErrorMessages={{
                                            exceedsMaxLength: `Value exceeds ${TEXT_INPUT_MAX_LENGTH} characters.`
                                        }}
                                        data-testid={`four-square-display-name-input-${item.displayKey}`}
                                        disabled={disabled}
                                        sx={{
                                            width: '100%',
                                            maxWidth: '250px',
                                            position: 'relative',
                                            top: '8px',
                                            alignSelf: 'flex-start',
                                            caretColor: 'auto'
                                        }}
                                    />
                                )}
                            </Box>
                        </ListItem>
                    ))
                ].filter(Boolean)}
            </List>
        </Box>
    );
};

export const FourSquareDisplayTab = React.memo(FourSquareDisplayTabBase);
